import { msg } from '@lingui/macro'

import * as React from 'react'

import { createArray } from '~logic/helpers/arrayUtils/createArray'
import { useTrans } from '~logic/i18n/useTrans'
import { createContext } from '~logic/lib/react'
import { useToast } from '~ui/components/toasts/useToast'

import { useProductDetailMachine } from '../../ProductDetail'
import { useCartUpdateCtx } from './CartContentProvider'

type TContext = {
  submitCartItem: () => void
  showValidationHint: () => void
}

const [Provider, useCartItemSubmitCtx] = createContext<TContext>(`CartItemSubmit`)

type TProps = RequiredChildren

/**
 * Provides product detail submit handler, that will either
 * add newly selected product to the cart
 * or update the existing cart item.
 */
export const SubmitCartItemProvider: React.FC<TProps> = ({ children }) => {
  const { trans } = useTrans()
  const [presentToast] = useToast()

  const [state, send] = useProductDetailMachine()
  const { addProductToCart, updateCartItems } = useCartUpdateCtx()

  const isEditingState = state.matches('loaded.editing')
  const canSubmit = isEditingState && state.context.addonCategoriesFulfilled

  // when the cart item can’t be submitted & user tries it, show some helpful message (eg. on hover)
  const showValidationHint = React.useCallback(() => {
    if (canSubmit) {
      return
    }

    presentToast({
      type: `info`,
      durationS: 2,
      message: trans(msg`Add required addons`),
    })
  }, [canSubmit, presentToast, trans])

  // add newly selected product to the cart or update the existing cart item
  const submitCartItem = React.useCallback(() => {
    if (!canSubmit || !state.context.product) {
      return
    }

    const { amount, addonCategories, product, note, editedItemIds, categoryId, promoTag } =
      state.context

    const addons = addonCategories.flatMap((category) =>
      category.addons.flatMap((addon) => {
        return createArray({
          length: addon.amount,
          value: { recipeId: addon.id, categoryId: category.id },
        })
      }),
    )

    // for existing cart items update their value
    if (editedItemIds !== null) {
      updateCartItems({
        cartItems: editedItemIds.map((id) => {
          return {
            id,
            addons,
            note,
          }
        }),
      })
    }

    //  add new products to the cart
    if (editedItemIds === null) {
      addProductToCart({
        amount,
        addons,
        note,
        product: { id: product.id },
        categoryId,
        promoTag,
      })
    }

    // inform the machine, that product was added/updated
    send({ type: 'ADD_ITEM_TO_CART' })
  }, [addProductToCart, canSubmit, send, state.context, updateCartItems])

  return <Provider value={{ submitCartItem, showValidationHint }}>{children}</Provider>
}

export { useCartItemSubmitCtx }
